import React, { useRef, useState } from "react";
import type { CallbackEvent } from "@shopify/polaris-types";

// ─── SectionCard ──────────────────────────────────────────────────────────────

export function SectionCard({ children }: { children: React.ReactNode }) {
  return (
    <s-box
      background="base"
      border="small subdued solid"
      borderRadius="large-100"
      padding="large-400"
    >
      {children}
    </s-box>
  );
}

export function CollapsibleSectionCard({
  header,
  children,
  defaultOpen = true,
}: {
  header: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  // Own the `open` state locally so parent re-renders (e.g. the user typing
  // in an unrelated field) don't snap the section back to `defaultOpen`.
  // React treats `<details open={...}>` as controlled and reconciles it
  // every render — passing the prop directly would reopen the card on
  // every keystroke. We seed from `defaultOpen` once, then keep state in
  // sync via the native `toggle` event so non-React triggers (e.g. clicks
  // on <summary>) also flow through.
  const [open, setOpen] = useState(defaultOpen);
  const detailsRef = useRef<HTMLDetailsElement>(null);

  return (
    <s-box
      background="base"
      border="small subdued solid"
      borderRadius="large-100"
      padding="none"
    >
      {/*
        `overflow-hidden` + an explicit radius on <details> makes it the
        clipping boundary for the summary's hover background and focus
        ring. We can't rely on `rounded-[inherit]` from the summary —
        <s-box>'s radius lives in its shadow DOM, so slotted children
        can't `inherit` it. Without this, the hover background bleeds
        past the card's rounded corners.
      */}
      <details
        ref={detailsRef}
        open={open}
        onToggle={() => {
          const next = detailsRef.current?.open ?? false;
          if (next !== open) setOpen(next);
        }}
        className="group rounded-xl overflow-hidden"
      >
        {/*
          Accessibility notes:
          - <details>/<summary> give us a disclosure role, keyboard
            toggle (Enter/Space), and live `open`-state announcement
            for free — no manual ARIA wiring needed.
          - focus-visible ring is explicit so keyboard users see focus
            against the card's white background (browser default
            outlines can disappear here).
          - The chevron is purely decorative (state lives on the
            <details> element) — aria-hidden keeps screen readers
            from announcing it.
          - The rotation animation respects prefers-reduced-motion.
        */}
        <summary className="cursor-pointer list-none [&::-webkit-details-marker]:hidden p-5 flex items-center justify-between gap-3 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-inset">
          <div className="flex-1 min-w-0">{header}</div>
          <span
            aria-hidden="true"
            className="shrink-0 text-gray-500 transition-transform duration-200 motion-reduce:transition-none group-open:rotate-180"
          >
            <s-icon type="chevron-down" />
          </span>
        </summary>
        <div className="px-5 pb-5">{children}</div>
      </details>
    </s-box>
  );
}

// ─── SectionHeader ────────────────────────────────────────────────────────────

export function SectionHeader({
  number,
  title,
  required = false,
}: {
  number: number;
  title: string;
  required?: boolean;
}) {
  return (
    <s-stack direction="inline" gap="small-200" alignItems="center">
      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold shrink-0">
        {number}
      </span>
      <s-text type="strong">{title}</s-text>
      {required && (
        <span
          className="text-red-500 font-semibold"
          title="Campo obligatorio"
          aria-label="Obligatorio"
        >
          *
        </span>
      )}
    </s-stack>
  );
}

// ─── Divider ──────────────────────────────────────────────────────────────────

export function Divider() {
  return <div className="h-px bg-gray-100 my-1" />;
}

// ─── TemplateCodes ────────────────────────────────────────────────────────────

export function TemplateCodes() {
  return (
    <s-stack direction="block" gap="small-100">
      <s-text color="subdued">Códigos disponibles:</s-text>
      <div className="flex items-center gap-2 flex-wrap">
        <code className="text-xs bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded font-mono border border-indigo-100">
          {"{product_name}"}
        </code>
        <code className="text-xs bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded font-mono border border-indigo-100">
          {"{first_name}"}
        </code>
      </div>
    </s-stack>
  );
}

// ─── ColorRow ─────────────────────────────────────────────────────────────────

export function ColorRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <s-color-field
      label={label}
      value={value}
      onInput={(e: CallbackEvent<"s-color-field">) =>
        onChange(e.currentTarget.value)
      }
    />
  );
}

// ─── NumericField ─────────────────────────────────────────────────────────────

export function NumericField({
  label,
  value,
  onChange,
  min,
  max,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
}) {
  const [raw, setRaw] = useState(String(value));
  const [isFocused, setIsFocused] = useState(false);
  const [syncedValue, setSyncedValue] = useState(value);

  // Derived-state sync (render-phase setState, not inside an effect).
  // When the parent resets `value` externally (e.g. discard) and the field
  // is not focused, align `raw` immediately. Calling setState during render
  // causes React to re-render with the new state without an extra commit.
  if (!isFocused && value !== syncedValue) {
    setSyncedValue(value);
    setRaw(String(value));
  }

  const commit = () => {
    const parsed = parseInt(raw, 10);
    const clamped = isNaN(parsed)
      ? (min ?? 0)
      : Math.max(min ?? -Infinity, Math.min(max ?? Infinity, parsed));
    onChange(clamped);
    setRaw(String(clamped));
    // Keep syncedValue aligned so the render-phase guard doesn't re-sync
    // immediately after blur with the stale pre-commit parent value.
    setSyncedValue(clamped);
  };

  return (
    <s-number-field
      label={label}
      min={min}
      max={max}
      value={raw}
      onFocus={() => setIsFocused(true)}
      onBlur={() => {
        setIsFocused(false);
        commit();
      }}
      onInput={(e: CallbackEvent<"s-number-field">) => {
        const v = e.currentTarget.value;
        setRaw(v);
        const parsed = parseInt(v, 10);
        if (!isNaN(parsed)) {
          onChange(
            Math.max(min ?? -Infinity, Math.min(max ?? Infinity, parsed))
          );
        }
      }}
    />
  );
}

// ─── ProductPickerButton ──────────────────────────────────────────────────────

export function ProductPickerButton({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected?: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 hover:border-indigo-300 hover:bg-indigo-50 transition-all text-left group"
    >
      <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-white border border-gray-200 shadow-sm group-hover:border-indigo-200 transition-all shrink-0">
        <s-icon type="plus" />
      </span>
      <div>
        <p className="text-sm font-medium text-gray-700 group-hover:text-indigo-700 transition-colors">
          {selected ?? label}
        </p>
        {selected && (
          <p className="text-xs text-gray-400 mt-0.5">Haz clic para cambiar</p>
        )}
      </div>
    </button>
  );
}
